/**
 * Radius theme profiles — shape tokens served from CMS.
 * Each profile defines a scale of radii (em) and behavior tokens (px).
 */

export const RADIUS_THEME_NAMES = {
  RECTANGULAR: 'rectangular',
  ROUNDED:     'rounded',
  PILL:        'pill',
}

// Shape scale tokens (em) — scale with --bim-base
// Each token changes value per profile
const RADIUS_THEME_TOKENS = {
  rectangular: {
    // Shape scale: raw radii (em)
    '--bim-radius-0':       '0em',
    '--bim-radius-xs':      '0em',
    '--bim-radius-s':       '0em',
    '--bim-radius-m':       '0em',
    '--bim-radius-l':       '0em',
    '--bim-radius-xl':      '0em',
    '--bim-radius-full':    '0em',
    '--bim-radius-forced':  '999px',

    // Semantic shape tokens: mapping to components
    '--bim-shape-control':        'var(--bim-radius-m)',
    '--bim-shape-control-small':  'var(--bim-radius-s)',
    '--bim-shape-control-medium':  'var(--bim-radius-m)',     // i.e. Inputs and smaller Buttons
    '--bim-shape-control-large':  'var(--bim-radius-l)',      // i.e. Button 3x3em
    '--bim-shape-control-xlarge':  'var(--bim-radius-xl)',    // i.e. ButtonHero
    '--bim-shape-chip':           'var(--bim-radius-xs)',
    '--bim-shape-card':           'var(--bim-radius-l)',
    '--bim-shape-card-inner':     'var(--bim-radius-m)',
    '--bim-shape-modal':          'var(--bim-radius-xl)',
    '--bim-shape-tooltip':        'var(--bim-radius-m)',
    '--bim-shape-popover':        'var(--bim-radius-l)',
    '--bim-shape-avatar':         'var(--bim-radius-forced)',
    '--bim-shape-legend-swatch':  'var(--bim-radius-0)',     // legend colour swatches — rect: square
    '--bim-shape-slider-thumb':   '0em',                     // slider / range thumb — rect: square corners
    '--bim-shape-needle':         '0em',                     // thin 0.4em needle for DirectionTicker — rect: square corners

    // Behavior tokens: layout behavior (px)
    '--bim-shape-separator':          '0px',
    '--bim-shape-overlay-offset':     '0px',
    '--bim-shape-card-footer-inset':  '0px',
    '--bim-shape-card-padding-x':     '0.75em',
    '--bim-shape-card-padding-y':     '0.75em',
    '--bim-shape-control-padding-x':  '0.75em',
    '--bim-shape-control-padding-y':  '0.5em',
    '--bim-shape-popover-inset':      '0px',
  },

  rounded: {
    // Shape scale (em)
    '--bim-radius-0':       '0em',
    '--bim-radius-xxs':      '0.25em',
    '--bim-radius-xs':      '0.5em',
    '--bim-radius-s':       '0.6em',
    '--bim-radius-m':       '0.75em',
    '--bim-radius-l':       '1em',
    '--bim-radius-xl':      '1.25em',
    '--bim-radius-full':    '999px',
    '--bim-radius-forced':  '999px',

    // Semantic shape tokens
    '--bim-shape-control':        'var(--bim-radius-m)',
    '--bim-shape-control-small':  'var(--bim-radius-s)',
    '--bim-shape-control-medium':  'var(--bim-radius-m)',     // i.e. Inputs and smaller Buttons
    '--bim-shape-control-large':  'var(--bim-radius-l)',      // i.e. Button 3x3em
    '--bim-shape-control-xlarge':  'var(--bim-radius-xl)',    // i.e. ButtonHero 4x4em
    '--bim-shape-chip':           'var(--bim-radius-xs)',
    '--bim-shape-card':           'var(--bim-radius-l)',
    '--bim-shape-card-inner':     'var(--bim-radius-m)',
    '--bim-shape-modal':          'var(--bim-radius-xl)',
    '--bim-shape-tooltip':        'var(--bim-radius-m)',
    '--bim-shape-popover':        'calc(var(--bim-radius-m) + var(--bim-shape-popover-inset))', // add inset to offset from CTA element radius
    '--bim-shape-avatar':         'var(--bim-radius-forced)',
    '--bim-shape-legend-swatch':  'var(--bim-radius-xxs)',    // legend colour swatches — rounded: slightly rounded
    '--bim-shape-slider-thumb':   'var(--bim-radius-xxs)',                  // slider / range thumb — small radius so 1.25em thumb stays square-ish, not circle
    '--bim-shape-needle':         '0.1em',                    // needle for DirectionTicker — micro radius so 0.4em needle stays square-ish,

    // Behavior tokens (px)
    '--bim-shape-separator':          '4px',
    '--bim-shape-overlay-offset':     '4px',
    '--bim-shape-card-footer-inset':  '4px',
    '--bim-shape-card-padding-x':     '0.875em',
    '--bim-shape-card-padding-y':     '0.875em',
    '--bim-shape-control-padding-x':  '0.875em',
    '--bim-shape-control-padding-y':  '0.625em',
    '--bim-shape-popover-inset':      '0.25em',
  },

  pill: {
    // Shape scale (em)
    '--bim-radius-0':       '0em',
    '--bim-radius-xs':      '0.8em',
    '--bim-radius-s':       '1em',
    '--bim-radius-m':       '1.25em',
    '--bim-radius-l':       '1.5em',
    '--bim-radius-xl':      '2em',
    '--bim-radius-full':    '999px',
    '--bim-radius-forced':  '999px',

    // Semantic shape tokens
    '--bim-shape-control':        'var(--bim-radius-full)',
    '--bim-shape-control-small':  'var(--bim-radius-s)',
    '--bim-shape-control-medium':  'var(--bim-radius-m)',     // i.e. Inputs and smaller Buttons
    '--bim-shape-control-large':  'var(--bim-radius-l)',      // i.e. Button 3x3em
    '--bim-shape-control-xlarge': 'var(--bim-radius-xl)',    // i.e. ButtonHero
    '--bim-shape-chip':           'var(--bim-radius-forced)',
    '--bim-shape-card':           'var(--bim-radius-l)',
    '--bim-shape-card-inner':     'var(--bim-radius-m)',
    '--bim-shape-modal':          'var(--bim-radius-xl)',
    '--bim-shape-tooltip':        'var(--bim-radius-m)',
    '--bim-shape-popover':        'calc(var(--bim-radius-l) + var(--bim-shape-popover-inset))', // add inset to offset from CTA element radius
    '--bim-shape-avatar':         'var(--bim-radius-forced)',
    '--bim-shape-legend-swatch':  'var(--bim-radius-forced)', // legend colour swatches — pill: capsule
    '--bim-shape-slider-thumb':   'var(--bim-radius-forced)', // slider / range thumb — pill: full circle
    '--bim-shape-needle':         'var(--bim-radius-forced)', // needle for DirectionTicker — pill: full circle

    // Behavior tokens (px)
    '--bim-shape-separator':          '8px',
    '--bim-shape-overlay-offset':     '8px',
    '--bim-shape-card-footer-inset':  '8px',
    '--bim-shape-card-padding-x':     '1em',
    '--bim-shape-card-padding-y':     '1em',
    '--bim-shape-control-padding-x':  '1em',
    '--bim-shape-control-padding-y':  '0.75em',
    '--bim-shape-popover-inset':      '0.25em',
  },
}

export function getRadiusThemeTokens(radiusTheme) {
  const themeName = radiusTheme || RADIUS_THEME_NAMES.RECTANGULAR
  return RADIUS_THEME_TOKENS[themeName] || RADIUS_THEME_TOKENS[RADIUS_THEME_NAMES.RECTANGULAR]
}